import i18n from 'i18n/config';

/**
 * Export file naming convention (single source of truth).
 *
 *      <Section>[_<Qualifier>...]_<YYYY-MM-DD>_<HHmmss>.<ext>
 *
 * e.g.  DriverTravelAllowances_2026-08-20_143205.xlsx            (en)
 *       ReisekostenerstattungenFürFahrer_2026-08-20_143205.xlsx  (de)
 *       Finanzen_Rechnungen_Inaktiv_2026-08-20_143205.xlsx       (de)
 *       Gehaltsabrechnung_Überstundendetails_2026-08-20_143205.xlsx
 *
 * Rules, and why:
 *  - The leading segments name the table/section exactly as the user sees it in the app, from
 *    broad to narrow (area, then section, then qualifier: `Inactive`, `My requests`, `Private`, ...).
 *    A file must be identifiable without opening it, so the name may never come from an internal
 *    identifier - `TableType` values are reused between screens, which is how "Driver travel
 *    allowances" used to export as `Sources_export_...`.
 *  - Each segment a call site passes is an i18n key, so the name is in the language the user is
 *    reading the app in. A segment with no translation falls back to the key itself, which is
 *    already the English label.
 *  - Segments are PascalCase and joined with `_`, so the section name reads as one token and the
 *    `_` boundaries stay meaningful (they separate section from qualifier from timestamp).
 *  - The timestamp is ISO 8601 (RFC 3339) local date + basic-format local time. Big-endian date
 *    first means a directory of exports sorts chronologically by name. `:` is omitted because it
 *    is not a legal file name character on Windows, which is what ISO 8601 basic format is for.
 *  - Only characters from the portable file name set survive sanitisation, so the name is safe on
 *    NTFS, ext4 and APFS alike and needs no escaping when re-used in a script or a URL.
 */

/**
 * Anything that is not a letter or a digit. Latin-1/Latin-Extended and Cyrillic letters are kept
 * so a German, Polish, Serbian or Turkish translation survives intact. The hyphen is kept too, but
 * only reaches this point when it is part of a period token - see `pascalCase`.
 */
const NON_WORD = /[^0-9A-Za-zÀ-ɏЀ-ӿ-]+/g;

/**
 * Segment separators a call site may use: `_` or " - ". Deliberately nothing else - a translated
 * label can itself contain "/" ("Profit/Cost centers" -> "Gewinn-/Kostenstellen"), and that is one
 * section, not two.
 */
const SEGMENT_SEPARATORS = /\s+-\s+|_+/;

/** Already an acronym or a version/period token (`TXT`, `2026-08`) - leave the casing alone. */
const KEEP_AS_IS = /^[A-Z0-9]+(-[A-Z0-9]+)*$/;

function pascalCase(segment: string): string {
    return segment
        .replace(/&/g, ' ')
        // A hyphen between digits is part of a period or a date (`2026-08`); anywhere else it is
        // a word break.
        .replace(/(\D)-|-(\D)/g, '$1 $2')
        .replace(NON_WORD, ' ')
        .trim()
        .split(/\s+/)
        .map(word => (KEEP_AS_IS.test(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)))
        .join('');
}

/**
 * Translates one segment into the UI language. The segment is an i18n key - the English label, as
 * used everywhere else in the app - so an untranslated segment simply stays English instead of
 * turning into a key-looking string.
 */
function translateSegment(segment: string): string {
    const translated = i18n.t(segment);
    return typeof translated === 'string' && translated.trim() ? translated : segment;
}

/**
 * Normalises a table or section name into the file name's leading segments, translating each
 * segment into the UI language on the way. Segments are i18n keys separated by `_` (or " - "), so
 * `"Finance_Invoices_Inactive"` becomes `Finanzen_Rechnungen_Inaktiv` for a German user and
 * `Finance_Invoices_Inactive` for an English one.
 */
export function toExportSectionName(raw: string | number | null | undefined): string {
    return String(raw ?? '')
        // "Foo (inactive)" -> "Foo_Inactive": parenthesised text is a qualifier, not part of the name.
        .replace(/\s*\(([^)]*)\)\s*/g, '_$1_')
        .split(SEGMENT_SEPARATORS)
        .map(segment => segment.trim())
        .filter(Boolean)
        .map(segment => pascalCase(translateSegment(segment)))
        .filter(Boolean)
        .join('_');
}

/** ISO 8601 local date + basic-format local time, e.g. `2026-08-20_143205`. */
export function formatExportTimestamp(date: Date = new Date()): string {
    const pad = (value: number) => value.toString().padStart(2, '0');
    const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const time = `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
    return `${day}_${time}`;
}

/**
 * Builds the default export file name (without extension) for a table or section.
 *
 * @param section    The table/section name as shown in the app, e.g. `'Driver travel allowances'`.
 * @param qualifiers Narrowing segments appended after the section, e.g. `'Inactive'`.
 */
export function buildExportFileName(
    section: string | number | null | undefined,
    qualifiers: (string | number | null | undefined)[] = [],
    at: Date = new Date()
): string {
    const segments = [section, ...qualifiers]
        .map(toExportSectionName)
        .filter(Boolean);

    // A section name is always expected; falling back to `Export` keeps the timestamp usable
    // rather than producing a file called `_2026-08-20_143205`.
    return `${segments.join('_') || 'Export'}_${formatExportTimestamp(at)}`;
}
